import React from 'react';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Menu, { MenuItem } from 'src/components/ui/Menu';
import Button from 'src/components/ui/Button';
import Dialog from 'src/components/ui/Dialog';
import DialogTitle from 'src/components/ui/DialogTitle';
import Icon from 'src/components/ui/Icon';
import IconButton from 'src/components/ui/IconButton';
import Typography from 'src/components/ui/Typography';
import Avatar from 'src/components/ui/Avatar';
import ReportModal from 'src/components/ui/ReportModal';

import { colours } from 'src/constants/colours';
import ConfirmModal from 'src/components/ui/ConfirmModal';
import useAlerts from 'src/hooks/useAlerts';
import useAppRouter from 'src/hooks/useAppRouter';
import useUser from 'src/hooks/useUser';
import useModalOpenState from 'src/hooks/useModalOpenState';
import { useDeleteStoryMutation, useAddBlockedUserMutation } from 'src/hooks/__generated__/queries';
import type { Medium, StoriesQuery, Owner, Company, User } from 'src/types/__generated__/graphql';

import formatDistanceToNow from 'src/utils/formatDistanceToNow';
import getOwnerLink from 'src/utils/getOwnerLink';
import useActivityToDisplay, { useCompanyActivityToDisplay } from 'src/hooks/useActivityToDisplay';
import { breakpoints } from 'src/constants/breakpoints';
import { styledComponent } from 'src/utils/styled';
import { AnalyticsEvent } from 'src/utils/analytics';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';

const StyledDialogTitle = styledComponent(DialogTitle)`
    padding-top: 1.5rem;
`;

const StyledConfirmationDialog = styledComponent(Dialog)`
    max-width: 36rem;
    margin: auto;
`;

const StyledConfirmModalDialogActions = styledComponent(DialogActions)`
    padding: 0 1.5rem 1.5rem 1.5rem;
`;

const StyledDialog = styledComponent(Dialog)`
    width: fit-content;
    margin: auto;
`;

const StyledTypography = styledComponent(Typography)`
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 0.875rem;
`;

const StyledName = styledComponent(Typography)`
    font-weight: 700;
    font-size: 0.875rem;
    line-height: 1.125rem;
`;

const Header = styledComponent(Box)`
    z-index: 1000;
    background: linear-gradient(180deg, rgba(19, 46, 72, 0.6) 0%, rgba(19, 46, 72, 0) 100%);
    p,
    h5 {
        color: ${colours.white.main};
    }
`;

const CloseIconContainer = styledComponent(Box)`
    margin-left: 0.25rem;

    @media (min-width: ${breakpoints.tabletPortrait}px) {
        display: none;
    }
`;

StyledDialog.defaultProps = {
    maxWidth: false,
};

interface Props {
    story: ArrayElement<NonNullable<StoriesQuery['stories']>>;
    setIsPaused?: (isPaused: boolean) => void;
    onClose?: () => void;
}

const StoryHeader: React.FC<Props> = ({ story, setIsPaused, onClose }) => {
    const { t, i18n } = useTranslation(['news', 'common', 'profile']);
    const router = useAppRouter();
    const currentUser = useUser();
    const getActivityToDisplay = useActivityToDisplay();
    const getCompanyActivityToDisplay = useCompanyActivityToDisplay();
    const { addAlert } = useAlerts();
    const [deleteStory] = useDeleteStoryMutation();
    const { logEventDynamic } = useDynamicLogEvents();

    const { props: reportModalProps, open: openReportModal } = useModalOpenState();
    const { props: deleteModalProps, open: openDeleteModal } = useModalOpenState();
    const { props: confirmModalProps, open: openconfirmModal } = useModalOpenState();

    const handleDeleteStory = async () => {
        await deleteStory({
            variables: {
                storyId: story.id,
            },
            refetchQueries: ['Stories', 'CurrentUserProfile', 'CompanyProfile'],
            awaitRefetchQueries: true,
        });
        deleteModalProps.onClose();
        setIsPaused?.(false);
        addAlert({
            type: 'success',
            message: t('news:deleteStorySuccess'),
        });
    };

    const [addBlockedUserMutation] = useAddBlockedUserMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted() {
            addAlert({
                type: 'success',
                message: t('profile:snackbarBlockUser', { name: story.owner.displayName }),
            });
        },
    });

    const handleAddBlockedContact = async () => {
        await addBlockedUserMutation({
            variables: {
                userId: story.owner.user?.id as string,
            },
            refetchQueries: ['NewsFeed', 'StoriesGroupedByOwners'],
            awaitRefetchQueries: true,
        });
        logEventDynamic(AnalyticsEvent.Block);
    };

    const ownerLink = getOwnerLink(story.owner as Owner);
    const isCompany = Boolean(story.owner.company?.id);
    const storyIsOwnerByCurrentUser = story.owner.user && story.owner.user.id === currentUser?.id;
    const storyIsOwnerByCurrentUserPublicCompany =
        story.owner.company && story.owner.company.id === currentUser?.affiliatedCompany?.id;
    const shouldShowReportInMenuItems = !storyIsOwnerByCurrentUser && !storyIsOwnerByCurrentUserPublicCompany;

    return (
        <Header
            width="100%"
            position="absolute"
            height="5.75rem"
            top="0.5rem"
            padding="1rem"
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
        >
            <Box display="flex" flexDirection="row" alignItems="center" height="2rem">
                <Avatar
                    alt={story.owner.displayName}
                    image={story.owner.image as Medium}
                    fallbackText={story.owner.initials}
                    style={{ cursor: 'pointer' }}
                    onClick={() => router.push(ownerLink)}
                    data-target-id="story-owner-avatar"
                    isCompany={isCompany}
                />
                <Box display="flex" flexDirection="column" mx={0.5}>
                    <Box display="flex" justifyContent="flex-start">
                        <StyledName
                            data-target="story-modal-text"
                            data-target-id="story-modal-author"
                            style={{ cursor: 'pointer' }}
                            onClick={() => router.push(ownerLink)}
                        >
                            {story.owner.displayName}
                        </StyledName>
                    </Box>
                    <Box display="flex" overflow="hidden">
                        <Box maxWidth="10rem">
                            <StyledTypography noWrap>
                                {isCompany
                                    ? getCompanyActivityToDisplay(story.owner.company as Company)
                                    : getActivityToDisplay(story.owner.user as User)}
                            </StyledTypography>
                        </Box>
                        <Box mx={0.5}>
                            <StyledTypography variant="body2" noWrap>
                                ·
                            </StyledTypography>
                        </Box>
                        <StyledTypography variant="body2" data-target-id="story-date-distance" noWrap>
                            {formatDistanceToNow(String(story.createdAt), i18n.language as SupportedLangs)}
                        </StyledTypography>
                    </Box>
                </Box>
            </Box>
            <Box display="flex">
                {currentUser && (
                    <Box height="2rem">
                        <Menu
                            id="simple-menu"
                            items={[
                                story.iHaveOwnership && (
                                    <MenuItem
                                        key="deleteStory"
                                        data-target="story-modal-menu"
                                        data-target-id="story-modal-menu-delete"
                                        onClick={openDeleteModal}
                                    >
                                        <Icon name="Delete" size="1rem" color={colours.primary.main} />
                                        <Box ml={1}>
                                            <Typography variant="h6">{t('news:deleteStory')}</Typography>
                                        </Box>
                                    </MenuItem>
                                ),
                                shouldShowReportInMenuItems && (
                                    <MenuItem
                                        key="reportStory"
                                        data-target="story-modal-menu"
                                        data-target-id="story-modal-menu-report"
                                        onClick={openReportModal}
                                    >
                                        <Icon name="Alert" size="1rem" />
                                        <Box ml={1}>
                                            <Typography variant="h6">{t('common:report.reportStory')}</Typography>
                                        </Box>
                                    </MenuItem>
                                ),
                                story.owner.user?.id !== currentUser.id && !story.owner.company && (
                                    <MenuItem
                                        key="blockUser"
                                        data-target="story-modal-menu"
                                        data-target-id="story-modal-menu-block"
                                        onClick={openconfirmModal}
                                    >
                                        <Icon name="Alert" size="1rem" />
                                        <Box ml={1}>
                                            <Typography variant="h6">{t('profile:blockUser')}</Typography>
                                        </Box>
                                    </MenuItem>
                                ),
                            ]}
                            extraOpenAction={() => setIsPaused?.(true)}
                            extraCloseAction={() => setIsPaused?.(false)}
                        >
                            <IconButton
                                data-target="story-modal-button"
                                data-target-id="story-modal-menu"
                                color="white"
                                size="small"
                                iconProps={{
                                    name: 'DotsHorizontalRounded',
                                    size: '1rem',
                                }}
                            />
                        </Menu>
                        {reportModalProps.open && (
                            <ReportModal
                                storyId={story.id}
                                open={reportModalProps.open}
                                onClose={() => {
                                    reportModalProps.onClose();
                                    setIsPaused?.(false);
                                }}
                            />
                        )}
                        <ConfirmModal
                            title={t('profile:titleBlockUser', {
                                name: story.owner.displayName,
                            })}
                            description={t('profile:descriptionBlockUser', {
                                name: story.owner.displayName,
                            })}
                            onClick={handleAddBlockedContact}
                            confirm={t('common:modal.confirm')}
                            {...confirmModalProps}
                        />
                        <StyledConfirmationDialog
                            open={deleteModalProps.open}
                            onClose={() => {
                                deleteModalProps.onClose();
                                setIsPaused?.(false);
                            }}
                        >
                            <StyledDialogTitle>
                                <Typography
                                    data-target="story-confirmation-modal-text"
                                    data-target-id="story-confirmation-title"
                                    variant="h2"
                                >
                                    {t('news:deleteStoryConfirmTitle')}
                                </Typography>
                            </StyledDialogTitle>
                            <DialogContent>
                                <Typography
                                    data-target="story-confirmation-modal-text"
                                    data-target-id="story-confirmation-content"
                                    color="dark"
                                >
                                    {t('news:deleteStoryConfirmContent')}
                                </Typography>
                            </DialogContent>
                            <StyledConfirmModalDialogActions>
                                <Button
                                    data-target="story-confirmation-modal-button"
                                    data-target-id="story-confirmation-cancel"
                                    onClick={() => {
                                        deleteModalProps.onClose();
                                        setIsPaused?.(false);
                                    }}
                                    variant="outlined"
                                >
                                    {t('common:modal.cancel')}
                                </Button>
                                <Button
                                    data-target="story-confirmation-modal-button"
                                    data-target-id="story-confirmation-delete"
                                    onClick={handleDeleteStory}
                                >
                                    {t('news:deleteStoryConfirmSubmit')}
                                </Button>
                            </StyledConfirmModalDialogActions>
                        </StyledConfirmationDialog>
                    </Box>
                )}
                <CloseIconContainer>
                    <Icon name="Close" size="2rem" color={colours.white.main} onClick={onClose} />
                </CloseIconContainer>
            </Box>
        </Header>
    );
};

export default StoryHeader;
